import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Lock, KeyRound, FileCheck2, ServerCog, ArrowLeft, ArrowRight, Activity, Menu, Check } from "lucide-react";
import smb1001Badge from "@/assets/smb1001-gold.png";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle, SheetClose } from "@/components/ui/sheet";
import { API_BASE } from "@/lib/api";
import { formatDateTime } from "@/lib/dates";

/**
 * Public /trust — same RoomMaster silhouette + RiskEye navy/cyan brand as Landing.
 * All uptime / status / certification data preserved.
 */
const STATUS_COLORS = {
  operational: "bg-emerald-500", degraded: "bg-amber-500", down: "bg-rose-500",
  unconfigured: "bg-slate-500", unmonitored: "bg-slate-600",
};
const STATUS_LABEL = {
  operational: "Operational", degraded: "Degraded", down: "Down",
  unconfigured: "Unconfigured", unmonitored: "Unmonitored",
};
const CERTS = [
  { name: "SMB1001 Gold Level 3", detail: "Independent posture certification for security-mature SMBs.", image: true },
  { name: "TLS 1.3 Everywhere",   detail: "Strict HSTS enforcement on every response.",                   icon: ShieldCheck },
  { name: "AES-256 at rest",      detail: "Every collection, every backup — encrypted at storage layer.", icon: Lock },
  { name: "Mandatory TOTP 2FA",   detail: "Fernet-encrypted secrets. No SMS fallback, no bypass.",         icon: KeyRound },
];
const SECURITY_CARDS = [
  { icon: ShieldCheck, title: "Enterprise-grade security", bullets: [
    "AES-256 encryption at rest across every tenant collection",
    "TLS 1.3 in transit with strict HSTS enforcement",
    "Continuous automated vulnerability + dependency scanning",
    "Penetration tested bi-annually by CREST auditors",
  ]},
  { icon: KeyRound, title: "Identity & access control", bullets: [
    "Mandatory TOTP two-factor on every sign-in",
    "Default-deny, per-role RBAC across every mutation",
    "Suspend a user or tenant from the super-admin console",
    "Signed HMAC invitation tokens with 24-hour expiry",
  ]},
  { icon: Lock, title: "Data privacy & integrity", bullets: [
    "SHA-256 hash-chained immutable audit log",
    "Encrypted 12-hour off-site backups with SHA verification",
    "Zero LLM training on customer registers or documents",
    "Sovereign Australian & regional storage options",
  ]},
];

function UptimeTimeline({ component }) {
  return (
    <div className="rounded-3xl border border-white/10 bg-[#0F1A38]/70 p-6" data-testid={`uptime-${component.key}`}>
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="font-display font-bold text-[18px] text-white leading-tight">{component.name}</div>
          <div className="text-[11px] font-mono text-slate-400 mt-1 tracking-wide">
            {component.monitored_days > 0 ? `${component.uptime_pct_90d}% · ${component.monitored_days} day${component.monitored_days === 1 ? "" : "s"} monitored` : "Monitoring just started"}
          </div>
        </div>
        <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10.5px] font-mono uppercase tracking-[0.2em] ${
          component.status === "operational" ? "text-emerald-300 bg-emerald-500/10 border-emerald-400/30" :
          component.status === "degraded" ? "text-amber-300 bg-amber-500/10 border-amber-400/30" :
          "text-slate-400 bg-slate-800 border-slate-700"
        }`}>
          <span className={`h-1.5 w-1.5 rounded-full ${STATUS_COLORS[component.status] || "bg-slate-500"}`} />
          {STATUS_LABEL[component.status] || component.status}
        </div>
      </div>
      <div className="flex gap-[2px] items-end h-10" role="img" aria-label={`${component.name} 90-day uptime`}>
        {component.timeline.map((e) => (
          <div key={e.date}
            className={`flex-1 h-full rounded-[2px] ${STATUS_COLORS[e.status] || "bg-slate-700"} ${e.status === "unmonitored" ? "opacity-40" : "hover:opacity-70"} transition-opacity`}
            title={`${e.date} — ${STATUS_LABEL[e.status] || e.status}`} />
        ))}
      </div>
      <div className="flex items-center justify-between mt-2 text-[10.5px] font-mono uppercase tracking-[0.2em] text-slate-500">
        <span>90 days ago</span><span>Today</span>
      </div>
    </div>
  );
}

export default function Trust() {
  const [statusData, setStatusData] = useState(null);
  const [openNav, setOpenNav] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { const r = await fetch(`${API_BASE}/status`, { credentials: "omit" }); if (r.ok && !cancelled) setStatusData(await r.json()); }
      catch (err) { console.warn("Trust: /status", err); }
    })();
    return () => { cancelled = true; };
  }, []);

  // SEO: per-route title, meta description + canonical.
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Trust & Security — RiskEye · TLS 1.3, AES-256, mandatory TOTP 2FA, SHA-256 audit trail";
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    desc?.setAttribute("content", "RiskEye trust posture: TLS 1.3, AES-256 at rest, Fernet-encrypted TOTP secrets, HMAC-hashed invitation tokens, SHA-256 hash-chained audit trail. SMB1001 Gold Level 3 certified. Mandatory TOTP 2FA on every sign-in.");
    const link = document.querySelector('link[rel="canonical"]');
    const prevHref = link?.getAttribute("href");
    link?.setAttribute("href", "https://riskeye.io/trust");
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
      if (link && prevHref != null) link.setAttribute("href", prevHref);
    };
  }, []);

  return (
    <div className="rm-landing min-h-screen bg-white" data-testid="trust">
      {/* Floating nav pill — mirrors Landing */}
      <header className="fixed top-4 inset-x-0 mx-auto max-w-6xl w-[94%] z-50 rounded-full border border-white/10 bg-[#0A1329]/90 backdrop-blur-xl">
        <div className="flex items-center justify-between px-4 sm:px-6 py-2.5">
          <Link to="/" className="flex items-center gap-2.5" data-testid="trust-brand">
            <div className="h-8 w-8 rounded-lg bg-riskeye-cyan flex items-center justify-center text-[#0A1329] font-bold text-sm shadow-[0_0_16px_rgba(34,211,238,0.5)]">R</div>
            <div className="leading-tight">
              <div className="font-display font-bold text-[16px] tracking-tight text-white">RiskEye</div>
              <div className="text-[8.5px] font-mono uppercase tracking-[0.2em] text-slate-400">by seQure</div>
            </div>
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-[13px] text-slate-300">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <Link to="/how-to-use" className="hover:text-white transition-colors">How it works</Link>
            <Link to="/trust" className="text-white">Trust</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:inline-flex text-[13px] font-medium text-slate-300 hover:text-white px-3 py-2 transition-colors">Sign in</Link>
            <a href="https://calendly.com/sequrenz/30min" target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 rounded-full bg-riskeye-cyan hover:bg-[#67E8F9] text-[#0A1329] text-[13px] font-semibold px-4 py-2 shadow-[0_0_20px_rgba(34,211,238,0.35)] transition-all">Book a demo <ArrowRight className="h-3.5 w-3.5" /></a>
            <Sheet open={openNav} onOpenChange={setOpenNav}>
              <SheetTrigger asChild><button className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-full text-slate-300 hover:bg-white/10"><Menu className="h-5 w-5" /></button></SheetTrigger>
              <SheetContent side="right" className="bg-[#0A1329] text-slate-100 border-white/10">
                <SheetHeader><SheetTitle className="text-white font-display">Menu</SheetTitle></SheetHeader>
                <div className="mt-6 flex flex-col gap-3">
                  <SheetClose asChild><Link to="/" className="text-lg py-2 border-b border-white/10">Home</Link></SheetClose>
                  <SheetClose asChild><Link to="/how-to-use" className="text-lg py-2 border-b border-white/10">How it works</Link></SheetClose>
                  <SheetClose asChild><Link to="/login" className="text-lg py-2 border-b border-white/10">Sign in</Link></SheetClose>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Hero — dark */}
      <section className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 bg-[#0A1329] text-white overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-riskeye-cyan/15 blur-3xl pointer-events-none" aria-hidden />
        <div className="absolute -bottom-40 -left-40 w-[460px] h-[460px] rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] text-emerald-300 mb-6" data-testid="trust-status">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" /> Live system of record · All systems operational
          </div>
          <h1 className="font-display text-4xl sm:text-6xl lg:text-[64px] font-bold leading-[1.05] tracking-tight max-w-4xl">
            Trust is a design decision, <span className="text-riskeye-cyan">not a checkbox.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
            Encryption on by default. TOTP mandatory on every sign-in. Every edit hash-chained. Every export signed. Every backup verified.
          </p>
        </div>
      </section>

      {/* Certifications — light */}
      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="inline-block rounded-full border border-riskeye-cyan/20 bg-riskeye-cyan/10 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] text-cyan-700 mb-10 font-semibold">Certifications</div>
          <div className="grid md:grid-cols-4 gap-5" data-testid="trust-certs">
            {CERTS.map((c) => (
              <div key={c.name} className="rounded-3xl border border-slate-200 bg-white p-6 hover:border-riskeye-cyan/60 hover:-translate-y-1 hover:shadow-[0_25px_50px_-25px_rgba(34,211,238,0.35)] transition-all">
                <div className="h-12 flex items-center mb-4">
                  {c.image ? <img src={smb1001Badge} alt={c.name} className="h-11 w-auto" /> : <c.icon className="h-6 w-6 text-cyan-700" strokeWidth={1.5} />}
                </div>
                <div className="font-display font-bold text-[17px] text-[#0A1329] leading-tight">{c.name}</div>
                <div className="mt-2 text-[13px] text-slate-600 leading-relaxed">{c.detail}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security card grid — light slate */}
      <section className="bg-slate-50 py-20 sm:py-28 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="inline-block rounded-full border border-riskeye-cyan/20 bg-riskeye-cyan/10 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] font-semibold text-cyan-700 mb-4">Security posture</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A1329] leading-[1.08]">A private system of record.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5" data-testid="trust-security-grid">
            {SECURITY_CARDS.map((c) => (
              <div key={c.title} className="rounded-3xl border border-slate-200 bg-white p-7 hover:border-riskeye-cyan/60 hover:-translate-y-1 transition-all" data-testid={`security-card-${c.title.split(" ")[0].toLowerCase()}`}>
                <div className="h-11 w-11 rounded-2xl bg-riskeye-cyan/10 border border-riskeye-cyan/30 flex items-center justify-center mb-5">
                  <c.icon className="h-5 w-5 text-cyan-700" strokeWidth={1.8} />
                </div>
                <h3 className="font-display font-bold text-[19px] text-[#0A1329] mb-4 tracking-tight">{c.title}</h3>
                <ul className="space-y-2.5">
                  {c.bullets.map((b) => (
                    <li key={b} className="flex gap-2.5 text-[13.5px] text-slate-600 leading-snug">
                      <Check className="h-4 w-4 text-riskeye-cyan mt-0.5 shrink-0" strokeWidth={2.2} /><span>{b}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Uptime timelines — dark */}
      <section className="bg-[#0A1329] py-20 sm:py-28 relative overflow-hidden">
        <div className="absolute top-1/2 -translate-y-1/2 -right-40 w-[420px] h-[420px] rounded-full bg-riskeye-cyan/10 blur-3xl pointer-events-none" aria-hidden />
        <div className="relative max-w-6xl mx-auto px-6">
          <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
            <div>
              <div className="inline-block rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] font-semibold text-emerald-300 mb-4">System status</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.08]">90 days of uptime.</h2>
            </div>
            {statusData?.updated_at && <div className="text-[11.5px] font-mono text-slate-400 mt-2"><Activity className="h-3.5 w-3.5 inline mr-1" /> Updated {formatDateTime(statusData.updated_at)}</div>}
          </div>
          {statusData?.components?.length ? (
            <div className="grid md:grid-cols-2 gap-5">{statusData.components.map((c) => <UptimeTimeline key={c.key} component={c} />)}</div>
          ) : (
            <div className="rounded-3xl border border-dashed border-white/10 bg-[#0F1A38]/40 p-10 text-center text-[13.5px] text-slate-500 font-mono">Live status feed is warming up…</div>
          )}
        </div>
      </section>

      {/* Contact CTA — light */}
      <section className="bg-white py-20 sm:py-28">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <div className="inline-block rounded-full border border-riskeye-cyan/20 bg-riskeye-cyan/10 px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.22em] font-semibold text-cyan-700 mb-4">Security contact</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight text-[#0A1329] leading-[1.08] mb-5">Coordinated disclosure, welcome.</h2>
            <p className="text-lg text-slate-600 leading-relaxed">
              Found something we should know about? Email <a href="mailto:security@riskeye.io" className="text-cyan-700 border-b border-cyan-700 hover:text-[#0A1329] hover:border-[#0A1329]">security@riskeye.io</a> — encrypted with our PGP key on request. We reply within one business day.
            </p>
          </div>
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-7">
            <ServerCog className="h-6 w-6 text-cyan-700 mb-4" strokeWidth={1.5} />
            <div className="font-display font-bold text-[19px] text-[#0A1329] leading-tight mb-3">Incident transparency</div>
            <p className="text-[13.5px] text-slate-600 leading-relaxed mb-4">Customer-impacting incidents are disclosed on this page within 24 hours with a post-mortem within seven days.</p>
            <div className="text-[13px] font-mono text-emerald-700 tracking-wide"><FileCheck2 className="h-4 w-4 inline mr-1.5" /> No incidents in the last 90 days.</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A1329] border-t border-white/5 text-slate-400">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 text-[11.5px] font-mono">
          <div>© {new Date().getFullYear()} seQure · RiskEye — This page is public.</div>
          <div className="flex gap-6"><Link to="/" className="hover:text-white">Home</Link><Link to="/how-to-use" className="hover:text-white">How to use</Link><Link to="/login" className="hover:text-white">Sign in</Link></div>
        </div>
      </footer>
    </div>
  );
}
